import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import { Button } from "@perfectserve/library";
import "@perfectserve/css-styles-react";
import { NetworkErrorsHeader, NetworkErrorsFilters, NetworkErrorsChart, NetworkErrorsTable, CallFlowModal, ActionFormModal, ActionsDropdown, } from "./components";
import { useNetworkErrorsData, useNetworkErrorsFilters } from "./hooks";
const NetworkErrors = ({ onInteractionClick, onInteractionDetailClick, onClose, className = "", }) => {
    const { dateRange, allInteractions, loading, error, availableActions, selectedActions, setSelectedActions, handleDateRangeChange, fetchErrorData, } = useNetworkErrorsData();
    const { interactions, groupedInteractions, excludeNoCoverage, excludeLooping, setExcludeNoCoverage, setExcludeLooping, filterCounts, chartGranularity, volumeChartData, } = useNetworkErrorsFilters(allInteractions, dateRange);
    const [showCallFlowModal, setShowCallFlowModal] = useState(false);
    const [selectedCallFlow, setSelectedCallFlow] = useState(null);
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState(null);
    const [actionFormData, setActionFormData] = useState(null);
    useEffect(() => {
        const style = document.createElement("style");
        style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes modalSlideIn {
        from { 
          opacity: 0; 
          transform: scale(0.9) translateY(-20px); 
        }
        to { 
          opacity: 1; 
          transform: scale(1) translateY(0); 
        }
      }
    `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            var _a, _b;
            if (dropdownOpenId &&
                !((_a = event.target) === null || _a === void 0 ? void 0 : _a.closest("[data-dropdown]")) &&
                !((_b = event.target) === null || _b === void 0 ? void 0 : _b.closest("[data-dropdown-portal]"))) {
                setDropdownOpenId(null);
                setDropdownPosition(null);
            }
        };
        const handleScroll = () => {
            if (dropdownOpenId) {
                setDropdownOpenId(null);
                setDropdownPosition(null);
            }
        };
        if (dropdownOpenId) {
            document.addEventListener("mousedown", handleClickOutside);
            window.addEventListener("scroll", handleScroll, true);
            window.addEventListener("resize", handleScroll);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
                window.removeEventListener("scroll", handleScroll, true);
                window.removeEventListener("resize", handleScroll);
            };
        }
        return undefined;
    }, [dropdownOpenId]);
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === "Escape" && showCallFlowModal) {
                handleCloseCallFlowModal();
            }
            if (event.key === "Escape" && dropdownOpenId) {
                setDropdownOpenId(null);
                setDropdownPosition(null);
            }
            if (event.key === "Escape" && actionFormData) {
                setActionFormData(null);
            }
        };
        if (showCallFlowModal || dropdownOpenId || actionFormData) {
            document.addEventListener("keydown", handleEscKey);
            return () => document.removeEventListener("keydown", handleEscKey);
        }
        return undefined;
    }, [showCallFlowModal, dropdownOpenId, actionFormData]);
    const handleRowClick = (interaction) => {
        if (onInteractionClick) {
            onInteractionClick(`Interaction Details - ${interaction.InteractionFinalAction}`, [interaction]);
        }
    };
    const handleChartDoubleClick = (data) => {
        if (data && data.activeLabel && onInteractionClick) {
            onInteractionClick(`${data.activeLabel} - Interactions`, data.interactions || []);
        }
    };
    const handleViewCallFlow = (callFlow) => {
        setSelectedCallFlow(callFlow);
        setShowCallFlowModal(true);
    };
    const handleCloseCallFlowModal = () => {
        setShowCallFlowModal(false);
        setSelectedCallFlow(null);
    };
    const handleActionButtonClick = (e, interactionId, index) => {
        e.stopPropagation();
        if (dropdownOpenId === interactionId) {
            setDropdownOpenId(null);
            setDropdownPosition(null);
        }
        else {
            const rect = e.currentTarget.getBoundingClientRect();
            const dropdownWidth = 200;
            const dropdownHeight = 180;
            const screenPadding = 16;
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            let leftPosition = rect.right - dropdownWidth;
            if (leftPosition < screenPadding) {
                leftPosition = screenPadding;
            }
            if (leftPosition + dropdownWidth > viewportWidth - screenPadding) {
                leftPosition = viewportWidth - dropdownWidth - screenPadding;
            }
            let topPosition = rect.bottom + 4;
            if (topPosition + dropdownHeight > viewportHeight - screenPadding) {
                topPosition = rect.top - dropdownHeight - 4;
            }
            if (topPosition < screenPadding) {
                topPosition = screenPadding;
            }
            setDropdownPosition({
                top: topPosition,
                left: leftPosition,
            });
            setDropdownOpenId(interactionId);
        }
    };
    const handleActionClick = (action) => {
        if (dropdownOpenId) {
            setActionFormData({
                interactionId: dropdownOpenId,
                action,
                description: "",
            });
            setDropdownOpenId(null);
        }
    };
    const handleActionSubmit = () => {
        if (actionFormData) {
            alert(`${actionFormData.action} created for interaction ${actionFormData.interactionId}${actionFormData.description ? `\nDescription: ${actionFormData.description}` : ""}`);
            setActionFormData(null);
        }
    };
    const handleActionCancel = () => {
        setActionFormData(null);
    };
    const handleActionDescriptionChange = (description) => {
        if (actionFormData) {
            setActionFormData({
                ...actionFormData,
                description,
            });
        }
    };
    if (loading) {
        return (_jsx("div", { className: `network-errors-container interactions-container ${className}`, children: _jsxs("div", { className: "network-errors-loading", children: [_jsx(FontAwesomeIcon, { icon: faClock, spin: true, className: "network-errors-loading-icon" }), _jsx("div", { className: "network-errors-loading-text", children: "Loading network error data..." })] }) }));
    }
    if (error) {
        return (_jsx("div", { className: `network-errors-container interactions-container ${className}`, children: _jsxs("div", { className: "network-errors-error", children: [_jsx(FontAwesomeIcon, { icon: faExclamationTriangle, className: "network-errors-error-icon" }), _jsx("div", { className: "network-errors-error-title", children: "Failed to load network error data" }), _jsx("div", { className: "network-errors-error-message", children: error }), _jsx(Button, { variant: "primary", onClick: fetchErrorData, children: "Retry" })] }) }));
    }
    return (_jsxs("div", { className: `network-errors-container interactions-container ${className}`, children: [_jsx(NetworkErrorsHeader, { onClose: onClose, dateRange: dateRange, onDateRangeChange: handleDateRangeChange, loading: loading }), _jsxs("div", { className: "interactions-card", style: {
                    margin: "0 0 20px",
                    padding: "24px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }, children: [_jsx(NetworkErrorsFilters, { availableActions: availableActions, selectedActions: selectedActions, onSelectedActionsChange: setSelectedActions, excludeNoCoverage: excludeNoCoverage, excludeLooping: excludeLooping, onExcludeNoCoverageChange: setExcludeNoCoverage, onExcludeLoopingChange: setExcludeLooping, filterCounts: filterCounts }), _jsx(NetworkErrorsChart, { volumeChartData: volumeChartData, chartGranularity: chartGranularity, interactions: interactions, onChartDoubleClick: handleChartDoubleClick }), _jsx(NetworkErrorsTable, { groupedInteractions: groupedInteractions, onRowClick: handleRowClick, onInteractionDetailClick: onInteractionDetailClick, onViewCallFlow: handleViewCallFlow, onActionButtonClick: handleActionButtonClick, dropdownOpenId: dropdownOpenId })] }), _jsx(CallFlowModal, { isOpen: showCallFlowModal, callFlow: selectedCallFlow, onClose: handleCloseCallFlowModal }), _jsx(ActionFormModal, { isOpen: !!actionFormData, actionFormData: actionFormData, onClose: handleActionCancel, onSubmit: handleActionSubmit, onDescriptionChange: handleActionDescriptionChange }), _jsx(ActionsDropdown, { isOpen: !!dropdownOpenId, dropdownOpenId: dropdownOpenId, dropdownPosition: dropdownPosition, onActionClick: handleActionClick, onClose: () => {
                    setDropdownOpenId(null);
                    setDropdownPosition(null);
                } })] }));
};
export default NetworkErrors;
//# sourceMappingURL=NetworkErrors.js.map